import { REDESIGN_AB_ROUTE_MAP, getLegacyTwinPathname } from 'root/src/client/v2/layout/helpers/redesignAbRoutes';
// Redesign routes that render standalone, with NO app chrome at all: no navbar (they mount none of
// their own either), no footer, no forced notification modal, no command palette. They still get the
// redesign theme and background, so they look like the app.
//
// The OAuth popup callback is the only one: it renders inside the 540x600 `window.open` popup that
// the opener tab closes as soon as the auth broadcast lands. App chrome there is noise, and a forced
// notification modal would be a trap in a window the user cannot navigate away from. Its URL is baked
// into the OAuth state (`routeUrls.authPopupCallback()`), so it is the redesign in place, with no
// `/nova` twin and no A/B map entry.
export const isRedesignChromelessRoute = (pathname) => pathname === '/auth/popup-callback';
// The Next.js error pages (`pages/404.js`, `pages/500.js`), both redesign in place: neither has a
// legacy twin to A/B against, so neither is in the A/B map. `/404` renders the real RedesignLayout and
// needs the legacy chrome hidden like any other redesign page. `/500` never reaches the chrome at all
// (`_app` short-circuits it and renders the page bare, outside every provider), so listing it here only
// buys the redesign body background under the standalone error shell.
export const isRedesignErrorRoute = (pathname) => pathname === '/404' || pathname === '/500';
// Redesign ("nova") routes: the chrome swap (hide the legacy Navigation / Footer, since each redesign
// page mounts its own RedesignNavbar / RedesignFooter, and paint the redesign background) keys off
// this. Sources:
//   - `/nova/*`: the redesign pages' own routes.
//   - `/design-preview` and `/drop-preview/[recordId]`: preview pages served in place. The proposal
//     share link `/drop-preview` renders the redesign drop page in preview mode for EVERYONE (the slug
//     is the shared secret), so it is not an A/B target and deliberately stays out of the A/B map, like
//     the throwaway `/design-preview`.
//   - `/reward/[rewardId]`: the reward-email destination. Born redesign-only, so it has no legacy page
//     to A/B against and no `/nova` twin, but it does mount its own RedesignNavbar and needs the legacy
//     chrome gone all the same.
//   - the LEGACY route patterns that now render the redesign in place (the keys of the A/B map). The
//     redesign became the default served AT the existing URLs (e.g. `/drops`, `/e/[recordId]`), so
//     those legacy patterns must count as redesign too, otherwise they would get the legacy chrome.
//   - the chrome-free redesign routes above, which need the legacy chrome hidden and the redesign
//     background just the same.
//   - the error routes above (`/404`, `/500`), for the same reason.
//
// `pathname` is the Next.js route pattern (`router.pathname`), not the resolved URL.
export const isRedesignRoute = (pathname) => (pathname.startsWith('/nova/')
    || pathname === '/design-preview'
    || pathname === '/drop-preview/[recordId]'
    || pathname === '/reward/[rewardId]'
    || pathname in REDESIGN_AB_ROUTE_MAP
    || isRedesignChromelessRoute(pathname)
    || isRedesignErrorRoute(pathname));
// Full-screen, app-like redesign pages with their own bottom nav that should NOT render the global
// RedesignFooter (it just dangles below the form). The add-campaign wizard is one, at either its
// redesign route or the legacy URL it now renders at. The auth pages are the others: their split
// layout already fills the viewport, and a marketing footer under a sign-in form only adds scroll.
// Chrome-free routes get no footer either.
const FOOTERLESS_PATHNAMES = new Set([
    '/add-campaign',
    '/nova/add-campaign',
    '/sign-in',
    '/nova/sign-in',
    '/sign-up',
    '/nova/sign-up',
    '/forgot-password',
    '/nova/forgot-password',
    '/reset-password',
    '/nova/reset-password',
    '/transfer-account',
    '/nova/transfer-account',
]);
export const isRedesignFooterlessRoute = (pathname) => (FOOTERLESS_PATHNAMES.has(pathname) || isRedesignChromelessRoute(pathname));
// Redesign pages that offer the Drift live chat, through RedesignLayout's own RedesignChatWidget. These
// are exactly the pages that used to carry `chatWidget: true` in their route description and got the
// legacy ChatWidget from <RenderModules /> (the sales-facing publisher surfaces plus the public drop
// page and its proposal preview). The description flag was dropped along with the redesign launcher, so
// this set is now the source of truth for them; routes still on the legacy design keep the flag.
//
// Keys are legacy `router.pathname` patterns; the `/nova` twins resolve through the A/B map, so a page
// reached at either URL gets the same chat.
const REDESIGN_CHAT_PATHNAMES = new Set([
    '/e/[recordId]',
    '/drop-preview/[recordId]',
    '/campaigns',
    '/whitelisted-creators',
    '/publisher-banned-creators',
]);
export const isRedesignChatRoute = (pathname) => {
    var _a;
    return (REDESIGN_CHAT_PATHNAMES.has(pathname)
        || REDESIGN_CHAT_PATHNAMES.has((_a = getLegacyTwinPathname(pathname)) !== null && _a !== void 0 ? _a : ''));
};
